import { useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import type { QueryObserverResult } from "@tanstack/query-core";
import { type DependencyList, useMemo } from "react";


export function useMappedQuery<TData, TError, TMapped>(
	mapFn: (data: TData) => TMapped,
	deps: DependencyList,
	query: QueryObserverResult<TData, TError>
): QueryObserverResult<TMapped, TError> {
	const mapFnRef = useUnsafeUpdatedRef(mapFn);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <controlled memoization>
	return useMemo(() => {
		return {
			...query,
			data: query.data !== undefined ? mapFnRef.current(query.data) : undefined,
			refetch: (options) => query
				.refetch(options)
				.then((fetched) => (fetched.data !== undefined ? mapFnRef.current(fetched.data) : undefined)),
		} as QueryObserverResult<TMapped, TError>;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		mapFnRef,
		query.status,
		query.dataUpdatedAt,
		// Note: query.data is not tracked as we don't care about reference equality but just about the data
		// being "fresh" which is already notified by the changing query.status/fetchStatus
		query.fetchStatus,
		query.isPaused,
		query.isRefetching,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		...deps,
	]);
}